import SurveyQrCode from '@components/SurveyQrCode/SurveyQrCode';
import Modal from '@components/v2/ui/Modal/Modal';
import Typography from '@components/v2/ui/Typography/Typography';
import { Stack } from '@mui/material';
import { ClipboardCheck, ExternalLink } from 'lucide-react';
import LangEnum from '../../../lang/LangEnum';
import { SatisfactionSurvey } from '../../../services/modules/satisfactionSurvey/satisfactionSurvey.types';
import { SatisfactionSurveyTestIds } from '../../../utils/testIds/SatisfactionSurvey.testIds';
import { useTranslation } from '../../translation/TranslationContext';

interface SatisfactionSurveyModalProps {
  isOpen: boolean;
  survey?: SatisfactionSurvey;
  onAnswerSurvey: () => void;
  onContinue: () => void;
  onCancel: () => void;
}

const QR_SIZE = 148;

const SatisfactionSurveyModal = ({
  isOpen,
  survey,
  onAnswerSurvey,
  onContinue,
  onCancel,
}: SatisfactionSurveyModalProps) => {
  const { translate } = useTranslation();

  if (!survey?.url) {
    return null;
  }

  const { url, message } = survey;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      isDismissable={false}
      size="standard"
      testId={SatisfactionSurveyTestIds.MODAL}
      header={{
        title: translate(LangEnum.SATISFACTION_SURVEY_MODAL_TITLE),
        icon: ClipboardCheck,
      }}
      footer={{
        primaryAction: {
          label: translate(LangEnum.SATISFACTION_SURVEY_MODAL_OPEN_LINK),
          href: url,
          endIcon: ExternalLink,
          testId: SatisfactionSurveyTestIds.SURVEY_LINK,
          onClick: onAnswerSurvey,
        },
        secondaryAction: {
          label: translate(LangEnum.GLOBAL_CONTINUE),
          testId: SatisfactionSurveyTestIds.CONTINUE_BUTTON,
          onClick: onContinue,
        },
      }}
    >
      <Stack spacing={3} alignItems="center">
        <Typography variant="body1" color="textSecondary" align="center">
          {message || translate(LangEnum.SATISFACTION_SURVEY_MODAL_DEFAULT_MESSAGE)}
        </Typography>

        <Stack spacing={1} alignItems="center">
          <SurveyQrCode url={url} size={QR_SIZE} testId={SatisfactionSurveyTestIds.QR_CODE} />
          <Typography aria-hidden variant="body2" color="textSecondary" align="center">
            {translate(LangEnum.SATISFACTION_SURVEY_MODAL_QR_HINT)}
          </Typography>
        </Stack>
      </Stack>
    </Modal>
  );
};

export default SatisfactionSurveyModal;
